/**
 * Audit interceptor helpers extracted from src/config/api.js.
 *
 * Exports three functions consumed by the axios interceptors in api.js:
 *   auditStampRequest(config)          – call in requestHandler (async)
 *   auditHandleResponse(response)      – call in responseHandler
 *   auditHandleError(req, status, msg) – call in errorHandler
 *
 * All private helpers and state live in this module.
 */

import authStore from '../../store/auth';
import { track } from './tracker';

// HTTP methods whose requests should be tracked in the audit log
const AUDIT_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// UUID pattern used for resource_id extraction (global for .match())
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
// Non-global version for boolean tests (avoids lastIndex side-effects)
const UUID_TEST = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

/**
 * Extract the resource id from a URL path by returning the last UUID segment.
 * e.g. "api/input-funds/653db90f-…" → "653db90f-…"
 */
function _resourceIdFromPath(path) {
  const matches = (path || '').match(UUID_RE);
  return matches ? matches[matches.length - 1] : null;
}

/**
 * Best-effort extraction of a human-readable name from an API response body.
 * Tries the most common field names across compliance resource types.
 */
function _resourceNameFromData(data) {
  if (!data || typeof data !== 'object') return null;
  const d = Array.isArray(data) ? data[0] : data;
  if (!d) return null;
  return (
    // Period objects: combine period + year into a readable label (e.g. "Q4 2025")
    (d.period != null && d.year != null ? `${d.period} ${d.year}` : null) ??
    d.name ??
    d.fund_name ??
    // Fund mapping responses: name lives inside nested input_fund object
    d.input_fund?.name ??
    d.username ??
    d.email ??
    d.category ??
    d.table_name ??
    // Jurisdiction / national-code responses use country_name (full) or reporting_member_state (code)
    d.country_name ??
    d.reporting_member_state ??
    // File / extraction / approver responses
    d.file_name ??
    d.filename ??
    d.workflow_name ??
    d.company_name ??
    null
  );
}

/**
 * In-memory cache of resource objects keyed by their UUID `id`.
 * Populated automatically from all GET responses so that before-state is
 * available for subsequent PUT/PATCH diffs without an extra network request.
 */
const MAX_CACHE = 2000;
const _resourceCache = new Map(); // id string → object

function _cacheItems(data) {
  if (!data || typeof data !== 'object') return;
  const items = Array.isArray(data) ? data : Array.isArray(data.items) ? data.items : [data];
  for (const item of items) {
    if (!item?.id) continue;
    if (_resourceCache.size >= MAX_CACHE) {
      _resourceCache.delete(_resourceCache.keys().next().value); // evict oldest
    }
    _resourceCache.set(String(item.id), item);
  }
}

/**
 * Fetch the current server-side state of a resource before a mutation so we
 * can include a before→after diff in the audit log.
 * Uses native fetch with a 1.5 s timeout so it never blocks the user.
 */
async function _prefetchState(path, authHeader, companyHeader) {
  try {
    const raw = import.meta.env?.VITE_BASE_URL ?? '';
    const base = raw && !raw.endsWith('/') ? `${raw}/` : raw;
    const url = `${base}${path}`;
    const headers = {};
    if (authHeader) headers['Authorization'] = authHeader;
    if (companyHeader) headers['X-Active-Company-Id'] = companyHeader;
    const controller = new AbortController();
    const tid = setTimeout(() => controller.abort(), 3000);
    const resp = await fetch(url, { headers, signal: controller.signal });
    clearTimeout(tid);
    if (!resp.ok) return null;
    const data = await resp.json();
    return data && typeof data === 'object' ? data : null;
  } catch (_) {
    return null;
  }
}

// Fields to skip when computing diffs (ids, timestamps, counters, status flags)
const DIFF_SKIP =
  /^(id$|created_at|modified_at|company_id|workflow_id|previous_period_id|is_|can_|file_count|fund_count|jurisdiction_count|national_code_count|automation_|report_task|file_sheet_stats|extraction_stats|transformation_stats)|(_progress_status|_status)$/;

/**
 * Compare two plain objects and return { from, to } containing only the
 * primitive-valued fields that actually changed. Returns null when identical.
 */
function _buildDiff(before, after) {
  if (!before || !after || typeof before !== 'object' || typeof after !== 'object') return null;
  if (Array.isArray(before) || Array.isArray(after)) return null;
  const from = {};
  const to = {};

  for (const key of Object.keys(before)) {
    if (DIFF_SKIP.test(key)) continue;
    const bVal = before[key];
    const aVal = after[key];
    if (aVal === undefined) continue;

    // Primitive changed
    if (typeof bVal !== 'object' && typeof aVal !== 'object') {
      if (bVal !== aVal) {
        from[key] = bVal;
        to[key] = aVal;
      }
      continue;
    }

    // Array: show count change
    if (Array.isArray(bVal) && Array.isArray(aVal)) {
      if (bVal.length !== aVal.length) {
        from[key] = `${bVal.length} items`;
        to[key] = `${aVal.length} items`;
      }
      continue;
    }

    // Shallow nested object: flatten one level as "parent.child"
    if (
      bVal &&
      aVal &&
      typeof bVal === 'object' &&
      typeof aVal === 'object' &&
      !Array.isArray(bVal) &&
      !Array.isArray(aVal)
    ) {
      for (const subKey of Object.keys(bVal)) {
        if (DIFF_SKIP.test(subKey)) continue;
        const bSub = bVal[subKey];
        const aSub = aVal[subKey];
        if (typeof bSub !== 'object' && typeof aSub !== 'object' && bSub !== aSub) {
          from[`${key}.${subKey}`] = bSub;
          to[`${key}.${subKey}`] = aSub;
        }
      }
    }
  }
  return Object.keys(from).length > 0 ? { from, to } : null;
}

/**
 * Safely parse the axios request body (config.data) which may be a JSON string
 * by the time the response interceptor runs.
 */
function _parseReqBody(config) {
  try {
    if (!config.data) return null;
    return typeof config.data === 'string' ? JSON.parse(config.data) : config.data;
  } catch {
    return null;
  }
}

/**
 * Build a table-level diff for extraction data.
 * Used when PUT /api/extract/{id}/ returns 204 (no response body).
 */
function _buildExtractionDiff(before, after) {
  try {
    const toTableData = data => {
      const tables = {};
      if (data?.non_funds?.data) {
        for (const [table, rows] of Object.entries(data.non_funds.data)) {
          tables[table] = Array.isArray(rows) ? rows : [];
        }
      }
      if (Array.isArray(data?.funds)) {
        for (const fund of data.funds) {
          if (fund?.data) {
            const prefix = fund.name ?? fund.id ?? 'fund';
            for (const [table, rows] of Object.entries(fund.data)) {
              tables[`${prefix} / ${table}`] = Array.isArray(rows) ? rows : [];
            }
          }
        }
      }
      return tables;
    };

    // Extract cell values (the v field) from the {v, y, x} cell format.
    // Limited to first 10 rows to keep the audit payload small.
    const extractRowValues = rows =>
      rows
        .filter(row => row && typeof row === 'object' && !Array.isArray(row))
        .slice(0, 10)
        .map(row =>
          Object.fromEntries(Object.entries(row).map(([col, cell]) => [col, cell?.v ?? cell]))
        );

    const bData = toTableData(before);
    const aData = toTableData(after);
    if (!Object.keys(bData).length && !Object.keys(aData).length) return null;

    const from = {},
      to = {};
    const allTables = new Set([...Object.keys(bData), ...Object.keys(aData)]);
    for (const t of allTables) {
      const bRows = bData[t] ?? null;
      const aRows = aData[t] ?? null;
      const bCount = bRows === null ? null : bRows.length;
      const aCount = aRows === null ? null : aRows.length;
      if (bCount !== aCount) {
        from[t] =
          bRows === null ? '(not present)' : { count: bCount, rows: extractRowValues(bRows) };
        to[t] = aRows === null ? '(removed)' : { count: aCount, rows: extractRowValues(aRows) };
      }
    }
    return Object.keys(from).length > 0 ? { from, to } : null;
  } catch (_) {
    return null;
  }
}

// Regex matching PUT /api/transform/{uuid} paths
const TRANSFORM_STEP_RE =
  /\/transform\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;

// For certain PUT paths the same-URL GET doesn't work. Map to the right read endpoint.
const PREFETCH_URL_OVERRIDES = [
  // update-national-code/{jurisdiction_id} → readable via get-national-code/{jurisdiction_id}
  [/api\/report\/update-national-code\/([^/?]+)/, m => `api/report/get-national-code/${m[1]}`]
];

/**
 * Returns true when the response body is only a status message
 * (e.g. {detail: "Updated successfully"}) and contains no resource data.
 */
function _isMessageOnlyBody(data) {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return false;
  const keys = Object.keys(data);
  return (
    keys.length <= 2 && !data.id && !data.name && (data.detail != null || data.message != null)
  );
}

/**
 * Build a diff for national code array updates.
 * Matches request-body items to cached before-state items by national_code_id → id.
 */
function _buildNationalCodeDiff(before, reqBody) {
  const beforeArr = Array.isArray(before) ? before : null;
  const reqArr = Array.isArray(reqBody) ? reqBody : null;
  if (!beforeArr || !reqArr) return null;

  const beforeById = {};
  for (const nc of beforeArr) {
    if (nc.id) beforeById[String(nc.id)] = nc;
  }

  const from = {},
    to = {};
  for (const update of reqArr) {
    const ncId = String(update.national_code_id || '');
    const prev = beforeById[ncId];
    if (!prev) continue;
    const label = prev.fund_name || ncId;
    const oldCode = prev.aif_national_code ?? '–';
    const newCode = update.aif_national_code ?? '–';
    if (String(oldCode) !== String(newCode)) {
      from[label] = oldCode;
      to[label] = newCode;
    }
  }
  return Object.keys(from).length > 0 ? { from, to } : null;
}

/**
 * Compare before/after step arrays for a transformation update.
 * Returns { action: 'CREATE'|'DELETE'|'UPDATE', diff: {from, to} | null }.
 */
function _inferStepsDiff(before, after) {
  const bSteps = Array.isArray(before?.steps) ? before.steps : [];
  const aSteps = Array.isArray(after?.steps) ? after.steps : [];

  const bIds = new Set(bSteps.map(s => s.id));
  const aIds = new Set(aSteps.map(s => s.id));

  const added = aSteps.filter(s => !bIds.has(s.id));
  const removed = bSteps.filter(s => !aIds.has(s.id));

  // The transformation itself is always being updated (a step was added/removed/changed).
  // The per-step transformation_action events carry the CREATE/DELETE action.
  const conceptualAction = 'UPDATE';

  const from = {};
  const to = {};
  if (bSteps.length !== aSteps.length) {
    from.step_count = String(bSteps.length);
    to.step_count = String(aSteps.length);
  }
  if (added.length) {
    from.added_actions = '–';
    to.added_actions = added.map(s => s.action).join(', ');
  }
  if (removed.length) {
    from.removed_actions = removed.map(s => s.action).join(', ');
    to.removed_actions = '–';
  }

  const hasDiff = Object.keys(from).length > 0;
  return {
    action: conceptualAction,
    diff: hasDiff ? { from, to } : null
  };
}

/** Returns 'PDF', 'XML', or null based on the export endpoint path. */
function _exportLabel(path) {
  if (/generate-pdf-report/.test(path)) return 'PDF';
  if (/create_xml_zip/.test(path)) return 'XML';
  return null;
}

/**
 * Extract period_id from an export URL.
 * For create_xml_zip/{uuid} it's in the path; for generate-pdf-report it's in ?period_id=.
 */
function _periodIdFromExportUrl(url) {
  const fromPath = _resourceIdFromPath(url);
  if (fromPath) return fromPath;
  try {
    const qi = (url || '').indexOf('?');
    return qi >= 0 ? new URLSearchParams(url.slice(qi)).get('period_id') : null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Stamp the axios request config with audit metadata (_auditStart, _auditBefore).
 * Call this at the top of requestHandler before the token/header logic.
 */
export async function auditStampRequest(config) {
  const url = config.url || '';
  if (
    AUDIT_METHODS.has((config.method || '').toLowerCase()) &&
    !url.includes('audit-logs/events') &&
    !url.includes('switch-company') &&
    !url.includes('user-groups/set-company')
  ) {
    config._auditStart = Date.now();

    const method = (config.method || '').toLowerCase();
    if (method === 'put' || method === 'patch') {
      // `edit-note/{stepId}` has no GET counterpart — the prefetch would only
      // ever 404, and the diff is trivially derivable from the request body.
      // Skip it to avoid the spurious GET users see in the network panel.
      if (/\/edit-note\//.test(url)) return config;
      const lastSeg = (config.url || '').split('?')[0].split('/').filter(Boolean).pop() ?? '';
      if (UUID_TEST.test(lastSeg)) {
        const cached = _resourceCache.get(lastSeg);
        if (cached) {
          config._auditBefore = cached;
        } else {
          let prefetchUrl = config.url;
          for (const [re, fn] of PREFETCH_URL_OVERRIDES) {
            const m = prefetchUrl.match(re);
            if (m) {
              prefetchUrl = fn(m);
              break;
            }
          }
          const token = authStore.token;
          const company = localStorage.getItem('activeCompanyId');
          config._auditBefore = await _prefetchState(
            prefetchUrl,
            token ? `Bearer ${token}` : null,
            company
          );
        }
      }
    }
  }
  return config;
}

/**
 * Cache GET response items and emit an audit event for mutating requests.
 * Call this at the top of responseHandler before returning response.data.
 */
export function auditHandleResponse(response) {
  if ((response.config.method || '').toLowerCase() === 'get') {
    try {
      _cacheItems(response.data);
    } catch (_) {
      /* swallow */
    }
  }

  if (response.config?._auditStart) {
    try {
      const path = response.config.url || '';
      const before = response.config._auditBefore;
      const afterData =
        (_isMessageOnlyBody(response.data) ? null : response.data) ||
        _parseReqBody(response.config);
      let diff = null;
      let actionOverride = null;
      const method = (response.config.method || '').toLowerCase();

      // Prefer the server-computed diff forwarded as a response header.
      // This is set by AuditDiffMiddleware for UPDATE endpoints that call _set_diff().
      const serverDiffHeader = response.headers?.['x-audit-diff'];
      if (serverDiffHeader) {
        try {
          diff = JSON.parse(serverDiffHeader);
        } catch (_) {
          /* ignore */
        }
      }

      if (!diff) {
        // Only infer steps diff for PUT/PATCH (updating transformation steps).
        // For DELETE on the transformation itself we want action = 'DELETE', not 'UPDATE'.
        if (
          TRANSFORM_STEP_RE.test(path) &&
          method !== 'delete' &&
          (before?.steps || afterData?.steps)
        ) {
          const stepResult = _inferStepsDiff(before, afterData);
          // The transformation itself is always being updated (a step was added/removed).
          actionOverride = stepResult.action;
          diff = stepResult.diff;
        }
        if (!diff) diff = _buildDiff(before, afterData);
        if (!diff) diff = _buildNationalCodeDiff(before, _parseReqBody(response.config));
        if (!diff) diff = _buildExtractionDiff(before, afterData);
      }

      // For POST requests the URL has no UUID, so fall back to response body id.
      const resourceId =
        _resourceIdFromPath(path) ||
        (method === 'post' && response.data?.id ? String(response.data.id) : null);

      const cachedResource = resourceId ? _resourceCache.get(resourceId) : null;
      const resourceName =
        _resourceNameFromData(response.data) ??
        _resourceNameFromData(before) ??
        _resourceNameFromData(afterData) ??
        _resourceNameFromData(cachedResource);

      // For PDF/XML export endpoints, enrich resource_name with the format label
      // and recover period_id from query params (generate-pdf-report passes it there).
      const exportLabel = _exportLabel(path);
      let finalResourceId = resourceId;
      let finalResourceName = resourceName;
      if (exportLabel) {
        const exportPeriodId = resourceId ?? _periodIdFromExportUrl(path);
        const periodCached = exportPeriodId ? _resourceCache.get(exportPeriodId) : null;
        const periodLabel = _resourceNameFromData(periodCached);
        finalResourceId = resourceId ?? exportPeriodId;
        finalResourceName = periodLabel ? `${periodLabel} (${exportLabel})` : exportLabel;
      }

      // For automate-report the period_id lives in the request body, not the URL.
      // Extract it so the audit log shows the correct period.
      const extraContext = {};
      if (/automate-report/.test(path)) {
        const reqBody = _parseReqBody(response.config);
        if (reqBody?.period_id) extraContext.period_id = String(reqBody.period_id);
        if (reqBody?.project_id) extraContext.project_id = String(reqBody.project_id);
      }

      track({
        http_method: (response.config.method || '').toUpperCase(),
        http_path: path,
        http_status: response.status,
        duration_ms: Date.now() - response.config._auditStart,
        status: 'SUCCEEDED',
        resource_id: finalResourceId,
        resource_name: finalResourceName,
        ...(actionOverride ? { action: actionOverride } : {}),
        metadata: {
          ...(diff ? { diff } : {}),
          ...(Object.keys(extraContext).length ? { context: extraContext } : {})
        }
      });

      // Emit a second event per affected transformation step action so the
      // activity log shows a dedicated "Transformation Action" resource-type row.
      // Skip for DELETE on the transformation itself — that's a full delete, not a step change.
      if (/\/transform/.test(path) && method !== 'delete') {
        const bSteps = Array.isArray(before?.steps) ? before.steps : [];
        const aSteps = Array.isArray((response.data || afterData)?.steps)
          ? (response.data || afterData).steps
          : [];
        const bIds = new Set(bSteps.map(s => s.id));
        const aIds = new Set(aSteps.map(s => s.id));

        const added = aSteps.filter(s => !bIds.has(s.id));
        const removed = bSteps.filter(s => !aIds.has(s.id));
        // For pure creates all steps are "added"
        const toLog = [
          ...added.map(s => ({ stepAction: 'CREATE', step: s })),
          ...removed.map(s => ({ stepAction: 'DELETE', step: s }))
        ];
        for (const { stepAction, step } of toLog) {
          const stepName = step.action
            ? step.action.charAt(0).toUpperCase() + step.action.slice(1).toLowerCase()
            : 'Action';
          track({
            action: stepAction,
            resource_type: 'transformation_action',
            resource_id: step.id ?? resourceId,
            resource_name: stepName,
            http_method: (response.config.method || '').toUpperCase(),
            http_path: path,
            http_status: response.status,
            status: 'SUCCEEDED'
          });
        }
      }
    } catch (_) {
      /* swallow */
    }
  }
}

/**
 * Emit an audit FAILED event for a mutating request that errored.
 * Call this inside errorHandler when originalRequest._auditStart is set.
 *
 * @param {object} originalRequest - the axios request config
 * @param {number} httpStatus      - HTTP status code (or 401 for auth failures)
 * @param {string|null} errorMessage - human-readable error message
 */
export function auditHandleError(originalRequest, httpStatus, errorMessage) {
  if (!originalRequest?._auditStart) return;
  try {
    const path = originalRequest.url || '';
    const resourceId = _resourceIdFromPath(path);

    // For export endpoints, look up period name from cache and add format label
    const exportLabel = _exportLabel(path);
    let finalResourceId = resourceId;
    let resourceName = null;
    if (exportLabel) {
      const exportPeriodId = resourceId ?? _periodIdFromExportUrl(path);
      const periodCached = exportPeriodId ? _resourceCache.get(exportPeriodId) : null;
      const periodLabel = _resourceNameFromData(periodCached);
      finalResourceId = resourceId ?? exportPeriodId;
      resourceName = periodLabel ? `${periodLabel} (${exportLabel})` : exportLabel;
    } else {
      const cachedResource = resourceId ? _resourceCache.get(resourceId) : null;
      resourceName = _resourceNameFromData(cachedResource);
    }

    track({
      http_method: (originalRequest.method || '').toUpperCase(),
      http_path: path,
      http_status: httpStatus,
      duration_ms: Date.now() - originalRequest._auditStart,
      status: 'FAILED',
      error_message: errorMessage ?? null,
      resource_id: finalResourceId,
      resource_name: resourceName
    });
  } catch (_) {
    /* swallow */
  }
}
